import { all, any, equals, map, pathOr, length, curry, has, path, find, propEq, flatten, cond, always, T } from "ramda";
import { conditionalFuncs } from "src/utilities/conditionalFn";
import { isFunction } from "src/utilities/isFunction";
import { ResourceFinder } from 'src/features/core/Intents/ApiContext/resourceFinder';
import { isEmptyOrNil } from "src/utilities/hasValue";
import { getConsentRule } from "../consent_rules";
import { getFormatters } from "../../ChatEngine/Formatters/formatters";
import { parsePathValue } from "../../ChatEngine/Configs/autoAnswerConfigurator";

const displayerMatcher = {
  matcher: (dis: any, arg: any) => equals(dis.match, arg.displayer),
  answerKey: 'value'
}


/*
This resourceMatcher is to build matcherFn for argEvaluator to find value from loan app resource 
example arg expected for this resourceMatcher
{
  "resource": "collateral",
  "resource_value": "collateral_information.collateral_type",
   resource_finder: {
      apply_rule: 'head',
      filter_option: {
        apply_logical: 'and',
        conditions: [{
          satisfies: "equals",
          arguments: [
            {
              data_path: "object_meta.labels.is_primary"
            },
            {
              target_value: YES_UPPERCASE_Y
            }
          ]
        }]
      }
  }
}
*/
export const getResourceMatcher = (loanApp: any) => {
  return {
    matcher: (_dis: any, arg: any) => {
      const resourceFinder: any = pathOr({}, ['resource_finder'], arg);
      const filteredResource = new ResourceFinder().getResource(
          resourceFinder, 
          pathOr({}, ["spec"], loanApp), 
          pathOr("", ['resource'], arg)
      )
      const resourceFormatter: string = pathOr('', ['resource_formatter'], arg);
      const resourceValue = parsePathValue(
        pathOr('', ['resource_value'], arg),
        filteredResource
      ) as any
 
      return resourceFormatter ? getFormatters(resourceFormatter)(resourceValue) : resourceValue;
    },
    answerKey: []
  }
}



interface IMatcher {
  answerKey: string | string[];
  matcher: (displayer: any, arg: any) => boolean;
  resourceFormatter?: string;
}
/**
 * 
 * @param displayers  - list of displayers or workflowIntents from urla or workflow config
 * @param conditions - list of conditions from saveAction config
 * @param applyLogicalCondition - apply logical condition example and
 * @param thisDisplayer { object } which is individual item which is configured the condition itself in this object
 * @param dm - dm have answerkey and Matcher that will check answer between displayers and current conditions values
 * @returns 
 * 
 * here checking if the condition is not present return false otherwise check the conditions value with displayers and return true
 */
export const executeConditions = (
  displayers: any,
  conditions: any,
  applyLogicalCondition: string,
  thisDisplayer: any = {},
  dm: IMatcher = displayerMatcher
) => {
  // If the conditions is empty then its false by default
  if (isEmptyOrNil(conditions)) {
    return false
  }
  const rules = map(
    (condition: any) => executeCondition(displayers, condition, thisDisplayer, dm)
  )(conditions)
  const fn = equals(applyLogicalCondition, 'and') ? all(equals(true)) : any(equals(true))
  return fn(rules)
}


const executeCondition = (displayers: any, cond: any, thisDisplayer: any, displayMatcher: IMatcher) => {
  const condType = pathOr('', ['satisfies'], cond);
  const argumnts = pathOr([], ['arguments'], cond);
  
  const args = evalArguments(displayers, argumnts, thisDisplayer, displayMatcher)
  if (length(args) <= 0) {
    return false
  }

  const result = curry(conditionalFuncs(condType))(...args)
  return isFunction(result) ? false : !!result;
}



const evalArguments = (
  displayers: any,
  argmts: any,
  thisDisplayer: any,
  dm: IMatcher) => {
  const isTargetValue = has('target_value')
  const isApply = has('apply')
  return map((arg: any) => {
    // flatten used in path because in some case we send single value or array string for nested path of value
    return cond([
      [isApply, always(getConsentRule(path(['apply'], arg)))],
      [isTargetValue, always(path(['target_value'], arg))],
      [propEq('displayer', 'this'), always(path(flatten([dm.answerKey]), thisDisplayer))],
      [(a) => has('resource', a) && has('resource_value', a), () => dm.matcher({}, arg)],
      [T, () => path(flatten([dm.answerKey]), find((dis: any) => dm.matcher(dis, arg))(displayers))]
    ])(arg)

  })(argmts)
}
